// Frontend/src/lib/notificationDisplay.ts
//
// Icon, tint, routing and relative-time formatting for a notification —
// shared by the bell's popover and the dedicated /resident/notifications
// page, so the two surfaces render the same event the same way.
//
// Kept out of NotificationBell.tsx itself: a component file that also
// exports plain constants and hooks trips react-refresh/only-export-
// components, same reason announcementStyles.ts and issueStyles.ts are
// separate from their pages.

import React from "react";
import {
  AlertTriangle,
  Bell,
  CreditCard,
  FileText,
  Megaphone,
  MessageSquare,
  Wrench,
  type LucideIcon,
} from "lucide-react";

import { useLanguage } from "@/contexts/LanguageContext";
import type { NotificationItem, NotificationKind } from "@/types/notification";

export type BellRole = "resident" | "hoa-admin" | "super-admin";

export const KIND_ICON: Record<NotificationKind, LucideIcon> = {
  ISSUE_CREATED: Wrench,
  ISSUE_STATUS_CHANGED: Wrench,
  ISSUE_COMMENTED: MessageSquare,
  ANNOUNCEMENT_PUBLISHED: Megaphone,
  INVOICE_ISSUED: FileText,
  INVOICE_REMINDER: AlertTriangle,
  PAYMENT_RECEIVED: CreditCard,
  PAYMENT_REJECTED: AlertTriangle,
  GENERAL: Bell,
  DOCUMENT_UPLOADED: FileText,
  OTP: Bell,
  SECURITY_ALERT: AlertTriangle,
  EMERGENCY: AlertTriangle,
};

export const KIND_TINT: Record<NotificationKind, string> = {
  ISSUE_CREATED: "#1F3A93",
  ISSUE_STATUS_CHANGED: "#1F3A93",
  ISSUE_COMMENTED: "#1F3A93",
  ANNOUNCEMENT_PUBLISHED: "#7C3AED",
  INVOICE_ISSUED: "#D97706",
  INVOICE_REMINDER: "#DC2626",
  PAYMENT_RECEIVED: "#27AE60",
  PAYMENT_REJECTED: "#DC2626",
  GENERAL: "#64748B",
  DOCUMENT_UPLOADED: "#1F3A93",
  OTP: "#64748B",
  SECURITY_ALERT: "#DC2626",
  EMERGENCY: "#DC2626",
};

/**
 * Where clicking a notification goes, per role.
 *
 * Returns null when there is nowhere sensible to send someone — a kind with
 * no route must still render and still mark read rather than crash or
 * navigate to a 404. Adding a producer without adding a route here is a
 * missing link, not a broken bell.
 */
export function routeFor(item: NotificationItem, role: BellRole): string | null {
  const id = item.target_object_id;
  if (item.kind === 'EMERGENCY') return `/${role}/notifications?notification=${encodeURIComponent(item.id)}`;
  if (item.kind === 'SECURITY_ALERT') return role === 'resident' ? '/resident/profile' : `/${role}/settings`;

  switch (item.target_type) {
    case "issues.issue":
      if (!id) return null;
      return role === "resident"
        ? `/resident/issues?issue=${id}`
        : `/hoa-admin/issues?issue=${id}`;

    case "announcements.announcement":
      // Admins read announcements on the communications tab; the standalone
      // /hoa-admin/announcements route is commented out in the router.
      return role === "resident"
        ? `/resident/announcements${id ? `?announcement=${id}` : ""}`
        : "/hoa-admin/communications?tab=announcements";

    case "files.document":
      return `/${role}/documents?${new URLSearchParams({
        ...(id ? { document: id } : {}),
        ...(item.target_building_id ? { building: item.target_building_id } : {}),
      })}`;

    case "payments.invoice":
      if (role === "resident") return "/resident/payments";
      return role === "super-admin" ? "/super-admin/payments" : "/hoa-admin/payments";

    default:
      return null;
  }
}

export function useRelativeTime() {
  const { t, language } = useLanguage();

  return React.useCallback(
    (iso: string) => {
      const seconds = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
      if (seconds < 60) return t("notifications.justNow");

      const units: [Intl.RelativeTimeFormatUnit, number][] = [
        ["minute", 60],
        ["hour", 3600],
        ["day", 86400],
      ];
      const formatter = new Intl.RelativeTimeFormat(language === "es" ? "es" : "en", {
        numeric: "auto",
      });

      for (let i = units.length - 1; i >= 0; i -= 1) {
        const [unit, size] = units[i];
        if (seconds >= size) return formatter.format(-Math.floor(seconds / size), unit);
      }
      return t("notifications.justNow");
    },
    [t, language]
  );
}
